import type {
  ConnectedAppScope,
  FirstPartyIntegration,
  FirstPartyIntegrationKey,
  FirstPartyIntegrationState,
} from '@molt/shared';
import {
  AlertTriangle,
  CheckCircle2,
  PlugZap,
  RefreshCw,
  Settings,
  ShieldAlert,
  Unplug,
} from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

import { Button, CardSurface, EmptyState, Notice, Skeleton } from '@/components';
import { RelativeTime } from '@/components/RelativeTime';
import { cn } from '@/lib/cn';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';

export interface FirstPartyIntegrationPanelProps {
  readonly integrations: readonly FirstPartyIntegration[] | undefined;
  readonly loading?: boolean | undefined;
  readonly loadError?: string | undefined;
  readonly canManage: boolean;
  readonly disabledReason: string | null;
  readonly busyKey?: FirstPartyIntegrationKey | null | undefined;
  readonly rowErrors?: Partial<Record<FirstPartyIntegrationKey, string>> | undefined;
  readonly onConnect: (key: FirstPartyIntegrationKey) => void;
  readonly onReconnect: (key: FirstPartyIntegrationKey) => void;
  readonly onDisconnect: (key: FirstPartyIntegrationKey) => void;
  readonly onConfigure: (key: FirstPartyIntegrationKey) => void;
  readonly onEnableOnBoard: (key: FirstPartyIntegrationKey) => void;
  readonly onDisableOnBoard: (key: FirstPartyIntegrationKey) => void;
  readonly onRetry?: (() => void) | undefined;
}

/**
 * FE-80.1 — these two were `SCOPE_LABELS` / `STATE_LABELS`, module-level
 * constants holding English, and a module constant cannot call `t`. The shipped
 * fix (`WorkspaceSwitcher`'s `ALL_WORKSPACES_KEY`, `WorkspaceMemberList`'s
 * `ROLE_LABEL_KEYS`) is that the CONSTANT HOLDS THE KEY and the render resolves
 * it.
 *
 * ⚠ Both `Record<…, string>` annotations stay EXPLICIT. They are the only thing
 * making a missing or misspelled arm a typecheck error: `t()` returns the key on
 * a miss rather than throwing, so a dropped arm would render
 * `integrations.firstParty.state.revoked` into the badge with nothing red.
 *
 * ⚠ The RECORDS' KEYS (`read_only`, `connected`, …) are wire values parsed by
 * `connectedAppScopeSchema` / `firstPartyIntegrationStateSchema` — they stay
 * literal. Only the VALUES are copy. Each arm is written as an inline literal so
 * the routing pin can read it out of the source text.
 */
const SCOPE_LABEL_KEYS: Record<ConnectedAppScope, string> = {
  read_only: 'integrations.firstParty.scope.readOnly',
  read_write: 'integrations.firstParty.scope.readWrite',
};

const STATE_LABEL_KEYS: Record<FirstPartyIntegrationState, string> = {
  not_configured: 'integrations.firstParty.state.notConfigured',
  connected: 'integrations.firstParty.state.connected',
  revoked: 'integrations.firstParty.state.revoked',
  no_access: 'integrations.firstParty.state.noAccess',
  unavailable: 'integrations.firstParty.state.unavailable',
};

/**
 * `t` is the FIRST parameter, matching the house idiom
 * (`BoardCustomFieldsSection`'s `settingDisabledReason`, `BoardCopyDialog`'s
 * `visibilityChoices`). Each arm keeps its own literal key inside a `t` call so
 * every value stays visible to the anchor the routing pin counts with.
 *
 * ⚠ No comment in this file writes that anchor out in its quoted form. The pin
 * scans SOURCE TEXT, not the AST, so a prose example would be read as a key and
 * fail the declared-key assertion — `interface-language.tsx:1345` is the live
 * instance of exactly that (FE-80.1 residual R12).
 */
function stateDescription(
  t: InterfaceLanguageContextValue['t'],
  integration: FirstPartyIntegration,
): string {
  switch (integration.state) {
    case 'connected':
      return integration.enabledOnBoard === false
        ? t('integrations.firstParty.stateBody.connectedNotEnabled')
        : t('integrations.firstParty.stateBody.connected');
    case 'revoked':
      return t('integrations.firstParty.stateBody.revoked');
    case 'no_access':
      return t('integrations.firstParty.stateBody.noAccess');
    case 'unavailable':
      return t('integrations.firstParty.stateBody.unavailable');
    case 'not_configured':
      return t('integrations.firstParty.stateBody.notConfigured');
  }
}

function stateIcon(state: FirstPartyIntegrationState): ReactNode {
  if (state === 'connected') {
    return <CheckCircle2 aria-hidden="true" className="size-4" />;
  }
  if (state === 'revoked') {
    return <RefreshCw aria-hidden="true" className="size-4" />;
  }
  if (state === 'no_access') {
    return <ShieldAlert aria-hidden="true" className="size-4" />;
  }
  if (state === 'unavailable') {
    return <AlertTriangle aria-hidden="true" className="size-4" />;
  }
  return <PlugZap aria-hidden="true" className="size-4" />;
}

function stateClassName(state: FirstPartyIntegrationState): string {
  if (state === 'connected') return 'border-ok text-ok';
  if (state === 'revoked' || state === 'unavailable') return 'border-warn text-warn';
  if (state === 'no_access') return 'border-bad text-bad';
  return 'border-border-strong text-ink-muted';
}

function boardState(
  t: InterfaceLanguageContextValue['t'],
  integration: FirstPartyIntegration,
): string | null {
  if (integration.enabledOnBoard === true)
    return t('integrations.firstParty.boardState.enabled');
  if (integration.enabledOnBoard === false)
    return t('integrations.firstParty.boardState.notEnabled');
  return null;
}

export function FirstPartyIntegrationPanel({
  integrations,
  loading = false,
  loadError,
  canManage,
  disabledReason,
  busyKey,
  rowErrors,
  onConnect,
  onReconnect,
  onDisconnect,
  onConfigure,
  onEnableOnBoard,
  onDisableOnBoard,
  onRetry,
}: FirstPartyIntegrationPanelProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const checking = loading || (integrations === undefined && loadError === undefined);
  const manageReason = canManage
    ? null
    : (disabledReason ?? t('integrations.firstParty.reason.editorOnly'));

  return (
    <section
      aria-labelledby="first-party-integrations-heading"
      className="flex flex-col gap-4"
    >
      <div className="flex flex-col gap-1">
        <h2
          id="first-party-integrations-heading"
          className="text-base font-semibold text-ink"
        >
          {t('integrations.firstParty.heading')}
        </h2>
        <p className="text-sm text-ink-muted">
          {t('integrations.firstParty.description')}
        </p>
      </div>

      {loadError !== undefined ? (
        <Notice
          tone="bad"
          title={t('integrations.firstParty.loadFailedTitle')}
          body={loadError}
          action={
            onRetry === undefined ? undefined : (
              <Button type="button" size="sm" variant="secondary" onClick={onRetry}>
                {t('integrations.firstParty.retry')}
              </Button>
            )
          }
        />
      ) : checking ? (
        <div aria-busy="true" className="grid gap-3">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} shape="block" className="h-48" />
          ))}
        </div>
      ) : integrations === undefined || integrations.length === 0 ? (
        <EmptyState
          tone="neutral"
          headingLevel={3}
          title={t('integrations.firstParty.emptyTitle')}
          body={t('integrations.firstParty.emptyBody')}
        />
      ) : (
        <div
          role="list"
          aria-label={t('integrations.firstParty.listLabel')}
          className="grid gap-3"
        >
          {integrations.map((integration) => {
            const busy = busyKey === integration.key;
            const busyProps = busyKey === undefined ? {} : { busy };
            const rowError = rowErrors?.[integration.key];
            return (
              <CardSurface
                key={integration.key}
                variant="sheet"
                role="listitem"
                aria-label={t('integrations.firstParty.rowLabel', {
                  name: integration.name,
                })}
                className="flex flex-col gap-3 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="break-words text-sm font-semibold text-ink">
                      {integration.name}
                    </h3>
                    <p className="text-sm text-ink-muted">{integration.description}</p>
                  </div>
                  <span
                    className={cn(
                      'inline-flex shrink-0 items-center gap-1 rounded-card border bg-surface px-2 py-1 text-xs font-medium',
                      stateClassName(integration.state),
                    )}
                  >
                    {stateIcon(integration.state)}
                    {t(STATE_LABEL_KEYS[integration.state])}
                  </span>
                </div>

                <p className="text-sm text-ink-muted">
                  {stateDescription(t, integration)}
                </p>

                <dl className="grid gap-2 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="text-xs text-ink-muted">
                      {t('integrations.firstParty.boardStatusLabel')}
                    </dt>
                    <dd className="text-ink">
                      {boardState(t, integration) ??
                        t('integrations.firstParty.accountScoped')}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-ink-muted">
                      {t('integrations.firstParty.accountLabel')}
                    </dt>
                    <dd className="text-ink">
                      {integration.connectedAccount === null
                        ? t('integrations.firstParty.noAccount')
                        : t(SCOPE_LABEL_KEYS[integration.connectedAccount.scope])}
                    </dd>
                  </div>
                  {integration.connectedAccount === null ? null : (
                    <>
                      <div>
                        <dt className="text-xs text-ink-muted">
                          {t('integrations.firstParty.grantedLabel')}
                        </dt>
                        <dd className="text-ink">
                          <RelativeTime value={integration.connectedAccount.grantedAt} />
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-ink-muted">
                          {t('integrations.firstParty.lastActiveLabel')}
                        </dt>
                        <dd className="text-ink">
                          {integration.connectedAccount.lastActiveAt === null ? (
                            t('integrations.firstParty.never')
                          ) : (
                            <RelativeTime
                              value={integration.connectedAccount.lastActiveAt}
                            />
                          )}
                        </dd>
                      </div>
                    </>
                  )}
                </dl>

                <div className="flex flex-wrap gap-2">
                  {integration.state === 'not_configured' ? (
                    <Button
                      type="button"
                      variant="primary"
                      disabled={manageReason !== null}
                      disabledReason={manageReason ?? undefined}
                      iconStart={<PlugZap aria-hidden="true" className="size-4" />}
                      onClick={() => onConnect(integration.key)}
                      {...busyProps}
                    >
                      {t('integrations.firstParty.connectAction', {
                        name: integration.name,
                      })}
                    </Button>
                  ) : integration.state === 'revoked' ? (
                    <Button
                      type="button"
                      variant="primary"
                      disabled={manageReason !== null}
                      disabledReason={manageReason ?? undefined}
                      iconStart={<RefreshCw aria-hidden="true" className="size-4" />}
                      onClick={() => onReconnect(integration.key)}
                      {...busyProps}
                    >
                      {t('integrations.firstParty.reconnectAction', {
                        name: integration.name,
                      })}
                    </Button>
                  ) : integration.state === 'connected' ? (
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={manageReason !== null}
                      disabledReason={manageReason ?? undefined}
                      iconStart={<Settings aria-hidden="true" className="size-4" />}
                      onClick={() => onConfigure(integration.key)}
                      {...busyProps}
                    >
                      {t('integrations.firstParty.configureAction', {
                        name: integration.name,
                      })}
                    </Button>
                  ) : null}

                  {integration.enabledOnBoard === false ? (
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={manageReason !== null}
                      disabledReason={manageReason ?? undefined}
                      onClick={() => onEnableOnBoard(integration.key)}
                      {...busyProps}
                    >
                      {t('integrations.firstParty.enableOnBoard')}
                    </Button>
                  ) : integration.enabledOnBoard === true ? (
                    <Button
                      type="button"
                      variant="quiet"
                      disabled={manageReason !== null}
                      disabledReason={manageReason ?? undefined}
                      onClick={() => onDisableOnBoard(integration.key)}
                      {...busyProps}
                    >
                      {t('integrations.firstParty.disableOnBoard')}
                    </Button>
                  ) : null}

                  {integration.connectedAccount === null ? null : (
                    <Button
                      type="button"
                      variant="quiet"
                      disabled={busy}
                      iconStart={<Unplug aria-hidden="true" className="size-4" />}
                      onClick={() => onDisconnect(integration.key)}
                      {...busyProps}
                    >
                      {t('integrations.firstParty.disconnectAction', {
                        name: integration.name,
                      })}
                    </Button>
                  )}
                </div>

                {rowError === undefined ? null : <Notice tone="bad" body={rowError} />}
              </CardSurface>
            );
          })}
        </div>
      )}
    </section>
  );
}
